/**
 * MODULE: _shared/frontend-shell/src/shell/hooks/useTheme.ts
 * PURPOSE: Light / dark / system appearance — same persistence and DOM rules as Settings Appearance tab
 * DEPENDENCIES: react (useCallback, useEffect, useState)
 * PUBLIC_API: useTheme, ThemeMode
 * SIDE_EFFECTS: document.documentElement class `dark`; localStorage `user_preferences`
 * AGENT_SAFETY: SAFE
 */

import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

function readSavedTheme(): ThemeMode {
  try {
    const raw = localStorage.getItem('user_preferences');
    const saved = raw ? (JSON.parse(raw) as { theme?: string }).theme : null;
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    /* ignore corrupt prefs */
  }
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  return 'system';
}

function applyThemeToDocument(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'dark') {
    root.classList.add('dark');
  } else if (mode === 'light') {
    root.classList.remove('dark');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) root.classList.add('dark');
    else root.classList.remove('dark');
  }
}

function persistTheme(mode: ThemeMode): void {
  try {
    const existing = localStorage.getItem('user_preferences');
    const prefs = existing ? JSON.parse(existing) : {};
    localStorage.setItem('user_preferences', JSON.stringify({ ...prefs, theme: mode }));
  } catch {
    /* ignore */
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeMode>('system');

  useEffect(() => {
    setThemeState(readSavedTheme());
  }, []);

  const apply = useCallback((mode: ThemeMode) => {
    applyThemeToDocument(mode);
  }, []);

  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyThemeToDocument('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode);
    persistTheme(mode);
    applyThemeToDocument(mode);
  }, []);

  return { theme, setTheme };
}
